/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ButtonFilterERP,
  ButtonPrimeReact,
  CalendarPrimeReact,
  Checkbox,
  SearchInput,
  TagERP,
} from "@finanedi.es/storybook"
import {
  faCircleCheck,
  faCircleXmark,
  faClock,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ByFilterAlbaranQueryParamsProps } from "api/Albaran.service"
import {
  ESTADO_REVISION_DOCUMENTO_COMERCIAL,
  EstadoRevisionDocumentoComercialType,
} from "api/Types/EstadoRevisionDocumentoComercial"
import { ESTADO_REVISION_DEFAULT } from "features/Documentos/pages/constants"
import { AnimatePresence } from "framer-motion"
import { Dialog } from "primereact/dialog"
import { InputNumber } from "primereact/inputnumber"
import { Dispatch, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { frontendCurrency } from "utils/formatCurrency"
import { backendDateFormat, frontendDateFormat } from "utils/formatDate"

const FilterMenu = ({
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  resetPagination,
}: {
  serviceQuery: Dispatch<Partial<ByFilterAlbaranQueryParamsProps>>
  sortingCriteria?: ByFilterAlbaranQueryParamsProps["SortingCriteria"]
  skipCount?: ByFilterAlbaranQueryParamsProps["SkipCount"]
  maxResultCount?: ByFilterAlbaranQueryParamsProps["MaxResultCount"]
  resetPagination: () => void
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [amount, setAmount] = useState<{
    min: number | undefined
    max: number | undefined
  }>({
    min: undefined,
    max: undefined,
  })
  const [estadoRevision, setEstadoRevision] = useState(ESTADO_REVISION_DEFAULT)
  const [keyword, setKeyword] = useState<string | undefined>()
  const [date, setDate] = useState<Record<string, string | undefined>>()
  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (queryParams: ByFilterAlbaranQueryParamsProps) => {
    const facturasParams = {
      EmailLibrado: queryParams.EmailLibrado,
      EmpresaActivaIdUsuario: queryParams.EmpresaActivaIdUsuario,
      EstadoRevision: queryParams.EstadoRevision,
      FechaEmisionLowerLimit: queryParams.FechaEmisionLowerLimit,
      FechaEmisionUpperLimit: queryParams.FechaEmisionUpperLimit,
      ImporteLowerLimit: queryParams.ImporteLowerLimit,
      ImporteUpperLimit: queryParams.ImporteUpperLimit,
      LibradoId: queryParams.LibradoId,
      LibradorId: queryParams.LibradorId,
      MaxResultCount: queryParams.MaxResultCount,
      MsgEncrypted: queryParams.MsgEncrypted,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "",
      Keyword: queryParams.Keyword,
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(facturasParams).filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
    )
    return fixedQueryParams
  }
  const handleQuery = () => {
    serviceQuery(
      queryFixer({
        FechaEmisionLowerLimit:
          date?.min && backendDateFormat(new Date(date.min)),
        FechaEmisionUpperLimit:
          date?.max && backendDateFormat(new Date(date.max)),
        ImporteLowerLimit: amount.min,
        ImporteUpperLimit: amount.max,
        EstadoRevision: Object.entries(estadoRevision)
          .filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
          .flatMap(
            ([key]) => key
          ) as unknown as EstadoRevisionDocumentoComercialType[],
        SortingCriteria: {
          property: sortingCriteria?.property,
          sort: sortingCriteria?.sort,
        },
        MaxResultCount: maxResultCount,
        SkipCount: skipCount,
        Keyword: keyword,
      })
    )
  }
  const handleApplyFilters = () => {
    setMenuOpen(false)
  }

  const toggleReviewedStatus = (key: EstadoRevisionDocumentoComercialType) => {
    setEstadoRevision({
      ...estadoRevision,
      [key]: !estadoRevision?.[key],
    })
  }

  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    setAmount({
      min: undefined,
      max: undefined,
    })
    setDate({ min: undefined, max: undefined })
    setEstadoRevision(ESTADO_REVISION_DEFAULT)
  }

  const appliedFiltersTags = [
    {
      label: t("filtros.appliedTag.amountFrom"),
      value: amount?.min ? frontendCurrency(amount?.min) : undefined,
      action: () => setAmount({ ...amount, min: undefined }),
    },
    {
      label: t("filtros.appliedTag.amountTo"),
      value: amount?.max ? frontendCurrency(amount?.max) : undefined,
      action: () => setAmount({ ...amount, max: undefined }),
    },
    {
      label: `${t("filterMenu.appliedTag.statusTag")}`,
      value: estadoRevision?.[ESTADO_REVISION_DOCUMENTO_COMERCIAL.PENDIENTE]
        ? t("filterMenu.invoice.pendingLabel")
        : undefined,
      action: () =>
        toggleReviewedStatus(ESTADO_REVISION_DOCUMENTO_COMERCIAL.PENDIENTE),
    },
    {
      label: `${t("filterMenu.appliedTag.statusTag")}`,
      value: estadoRevision?.[ESTADO_REVISION_DOCUMENTO_COMERCIAL.VALIDADO]
        ? t("filterMenu.invoice.approvedLabel")
        : undefined,
      action: () =>
        toggleReviewedStatus(ESTADO_REVISION_DOCUMENTO_COMERCIAL.VALIDADO),
    },
    {
      label: `${t("filterMenu.appliedTag.statusTag")}`,
      value: estadoRevision?.[ESTADO_REVISION_DOCUMENTO_COMERCIAL.RECHAZADO]
        ? t("filterMenu.invoice.rejectedLabel")
        : undefined,
      action: () =>
        toggleReviewedStatus(ESTADO_REVISION_DOCUMENTO_COMERCIAL.RECHAZADO),
    },
    {
      label: `${t("filtros.appliedTag.dateFrom")}`,
      value: date?.min ? frontendDateFormat(new Date(date.min)) : undefined,
      action: () => setDate({ ...date, min: undefined }),
    },
    {
      label: `${t("filtros.appliedTag.dateTo")}`,
      value: date?.max ? frontendDateFormat(new Date(date.max)) : undefined,
      action: () => setDate({ ...date, max: undefined }),
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, appliedFilters?.length])

  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    keyword,
    appliedFilters?.length,
    amount?.min,
    amount?.max,
    date?.min,
    date?.max,
    sortingCriteria?.sort,
    sortingCriteria?.property,
    skipCount,
    maxResultCount,
  ])

  return (
    <>
      <ButtonFilterERP
        isActive={appliedFilters.length > 0}
        numberActive={appliedFilters?.length}
        onClick={openFilters}
        deleteFilter={handleResetFilters}
        filterButtonLabel={`${t("filtros.title")}`}
      />
      <SearchInput
        placeholder={`${t("filtros.placeholderSearch")}`}
        onChange={(ev: string) => {
          resetPagination?.()
          setKeyword(ev)
        }}
        inputValue={keyword}
        className='tw-h-10 tw-w-full sm:tw-w-72'
      />
      <AnimatePresence>
        <Dialog
          breakpoints={{ "960px": "75vw" }}
          className='p-dialog-right'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonPrimeReact
                className='tw-ml-2 !tw-bg-primary tw-cursor-pointer !tw-w-full'
                onClick={handleApplyFilters}
              >
                {t("filtros.labelApply")}
              </ButtonPrimeReact>
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='flex tw- tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("filterMenu.appliedTag.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("filterMenu.appliedTag.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={`${filter.label}-${filter.value}`}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filtros.importe.title")}
              </span>
            </div>
            <div className='tw-flex tw-flex-row tw-mb-8'>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='amountMin'>
                  {t("filtros.importe.fromLabel")} (€)
                </label>
                <InputNumber
                  name='amountMin'
                  className='[&_input]:!tw-w-36 !tw-w-36'
                  value={amount.min}
                  placeholder='0,00 €'
                  onValueChange={(e) => setAmount({ ...amount, min: e.value })}
                  minFractionDigits={2}
                  min={0}
                  max={amount.max ?? Number.MAX_SAFE_INTEGER}
                  mode='currency'
                  currency='EUR'
                />
              </div>
              <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='amountMax'>
                  {t("filtros.importe.toLabel")} (€)
                </label>
                <InputNumber
                  name='amountMax'
                  className='[&_input]:!tw-w-36 !tw-w-36'
                  value={amount.max}
                  placeholder='0,00 €'
                  onValueChange={(e) => setAmount({ ...amount, max: e.value })}
                  minFractionDigits={2}
                  min={amount?.min ?? 0}
                  max={Number.MAX_SAFE_INTEGER}
                  mode='currency'
                  currency='EUR'
                />
              </div>
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filterMenu.invoice.statusAprov")}
              </span>
            </div>
            <div className='tw-flex tw-items-center tw-mb-4'>
              <Checkbox
                id='pending'
                className='tw-mr-4'
                isChecked={
                  estadoRevision?.[
                    ESTADO_REVISION_DOCUMENTO_COMERCIAL.PENDIENTE
                  ]
                }
                onClick={() =>
                  toggleReviewedStatus(
                    ESTADO_REVISION_DOCUMENTO_COMERCIAL.PENDIENTE
                  )
                }
              />
              <TagERP
                value={t("filters.estadoRevision.pendingLabel")}
                color='alternativeOne'
                icon={<FontAwesomeIcon icon={faClock} />}
              />
            </div>
            <div className='tw-flex tw-items-center tw-mb-4'>
              <Checkbox
                id='validated'
                className='tw-mr-4'
                isChecked={
                  estadoRevision?.[ESTADO_REVISION_DOCUMENTO_COMERCIAL.VALIDADO]
                }
                onClick={() =>
                  toggleReviewedStatus(
                    ESTADO_REVISION_DOCUMENTO_COMERCIAL.VALIDADO
                  )
                }
              />
              <TagERP
                value={t("filters.estadoRevision.validateLabel")}
                color='success'
                icon={<FontAwesomeIcon icon={faCircleCheck} />}
              />
            </div>
            <div className='tw-flex tw-items-center tw-mb-4'>
              <Checkbox
                id='rejected'
                className='tw-mr-4'
                isChecked={
                  estadoRevision?.[
                    ESTADO_REVISION_DOCUMENTO_COMERCIAL.RECHAZADO
                  ]
                }
                onClick={() =>
                  toggleReviewedStatus(
                    ESTADO_REVISION_DOCUMENTO_COMERCIAL.RECHAZADO
                  )
                }
              />
              <TagERP
                value={t("filters.estadoRevision.rejectedLabel")}
                color='danger'
                icon={<FontAwesomeIcon icon={faCircleXmark} />}
              />
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filterMenu.invoice.issueDate.title")}
              </span>
            </div>
            <div className='tw-flex tw-flex-row tw-mb-6'>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='fechaDesde'>
                  {t("filterMenu.invoice.issueDate.since")}
                </label>
                <CalendarPrimeReact
                  id='fechaDesde'
                  name='fechaDesde'
                  clearButtonClassName='!tw-hidden'
                  value={date?.min ? new Date(date.min) : undefined}
                  locale={language}
                  onChange={(e) => {
                    const eventDate = e.target.value as unknown as string
                    setDate({
                      max:
                        date?.max && new Date(eventDate) > new Date(date?.max)
                          ? undefined
                          : date?.max,
                      min: eventDate,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  className='tw-w-full'
                  baseZIndex={2302}
                  showIcon
                  maxDate={date?.max ? new Date(date.max) : undefined}
                />
              </div>
              <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='fechaHasta'>
                  {t("filterMenu.invoice.issueDate.to")}
                </label>
                <CalendarPrimeReact
                  id='fechaHasta'
                  name='fechaHasta'
                  clearButtonClassName='!tw-hidden'
                  value={date?.max ? new Date(date.max) : undefined}
                  locale={language}
                  onChange={(e) => {
                    const eventDate = e.target.value as unknown as string
                    setDate({
                      min:
                        date?.min && new Date(eventDate) < new Date(date?.min)
                          ? undefined
                          : date?.min,
                      max: eventDate,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  className='tw-w-full'
                  baseZIndex={2302}
                  showIcon
                  minDate={date?.min ? new Date(date.min) : undefined}
                />
              </div>
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}

export default FilterMenu
